import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import {
  LockOutlined,
  Visibility,
  VisibilityOff,
  DoneAll,
  CheckCircle,
  RadioButtonUnchecked,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { validatePasswordStrength } from '../utils/passwordValidation';
import AuthLayout from './auth/AuthLayout';
import {
  authColors,
  fontStack,
  AuthField,
  AuthButton,
} from './auth/authStyles';

const ResetPassword = ({ token, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, errors: [] });
  const { login } = useAuth();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  useEffect(() => {
    if (password) {
      setPasswordValidation(validatePasswordStrength(password));
    } else {
      setPasswordValidation({ isValid: false, errors: [] });
    }
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordValidation.isValid) {
      setError('Le mot de passe ne respecte pas les critères de sécurité');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/reset-password`,
        { token, password },
        { withCredentials: true }
      );
      login(response.data.user, response.data.token);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const rules = [
    { label: 'Au moins 8 caractères', ok: password.length >= 8 },
    { label: 'Une majuscule et une minuscule', ok: /[A-Z]/.test(password) && /[a-z]/.test(password) },
    { label: 'Un chiffre', ok: /[0-9]/.test(password) },
    {
      label: 'Un caractère spécial (recommandé)',
      ok: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    },
  ];

  return (
    <AuthLayout
      brandTitle="Choisissez un mot de passe fort."
      brandSubtitle="Votre nouvelle clé d'accès est personnelle. 8 caractères minimum, une majuscule et un chiffre pour une protection optimale."
      error={error}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
        {/* En-tête */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ color: authColors.textPrimary, fontSize: { xs: '23px', md: '27px' }, fontWeight: 700, lineHeight: 1.2, fontFamily: fontStack }}>
            Définir un nouveau mot de passe
          </Typography>
          <Typography sx={{ color: authColors.textMuted, fontSize: '13px', lineHeight: 1.5, fontFamily: fontStack }}>
            Choisissez un mot de passe unique que vous n'utilisez nulle part ailleurs.
          </Typography>
        </Box>

        <AuthField
          label="Nouveau mot de passe"
          icon={<LockOutlined />}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••••••"
          required
          autoComplete="new-password"
          endIcon={showPassword ? <VisibilityOff /> : <Visibility />}
          onEndIconClick={() => setShowPassword(!showPassword)}
        />

        <AuthField
          label="Confirmer le mot de passe"
          icon={<DoneAll sx={{ color: confirmPassword && password === confirmPassword ? authColors.accentGreen : authColors.textPlaceholder }} />}
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••••••••"
          required
          autoComplete="new-password"
          endIcon={showConfirmPassword ? <VisibilityOff /> : <Visibility />}
          onEndIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
        />

        {/* Critères de sécurité */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            p: '16px',
            borderRadius: '12px',
            backgroundColor: '#24253A',
            border: `1px solid ${authColors.borderSubtle}`,
          }}
        >
          <Typography sx={{ color: authColors.textPlaceholder, fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', fontFamily: fontStack }}>
            CRITÈRES DE SÉCURITÉ
          </Typography>
          {rules.map((rule) => (
            <Box key={rule.label} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {rule.ok ? (
                <CheckCircle sx={{ fontSize: 14, color: authColors.accentGreen }} />
              ) : (
                <RadioButtonUnchecked sx={{ fontSize: 14, color: authColors.textPlaceholder }} />
              )}
              <Typography
                sx={{
                  color: rule.ok ? authColors.textPrimary : authColors.textMuted,
                  fontSize: '12px',
                  fontFamily: fontStack,
                }}
              >
                {rule.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <AuthButton loading={loading} disabled={!passwordValidation.isValid}>
          {!loading && <VerifiedUserOutlined sx={{ fontSize: 16 }} />}
          {loading ? 'Réinitialisation...' : 'Mettre à jour mon mot de passe'}
        </AuthButton>
      </Box>
    </AuthLayout>
  );
};

export default ResetPassword;
